// src/components/DashboardLayout.tsx
import React from 'react';
import { PanelWrapper } from './PanelWrapper';
import { KPIStrip, KPICardData } from './KPIStrip';
import { ListPanel, ListRowData } from './ListPanel';
import { useFocusMode } from '../context/FocusModeContext';
import { useDemoMode } from '../context/DemoModeContext';
import { usePanelHierarchy } from '../hooks/usePanelHierarchy';

// Demo data
const demoKPICards: KPICardData[] = [
  { id: 'spend', label: 'TOTAL SPEND', value: '£1,240', subline: 'civo £890 · hetzner £230 · hostinger £120', state: 'normal', sparklineData: [100, 120, 95, 140, 130, 160, 150, 180, 170, 200] },
  { id: 'apps', label: 'APPS ONLINE', value: '12/12', subline: 'all providers responding', state: 'ok' },
  { id: 'vms', label: 'LIVE VMS', value: '8', subline: 'civo cluster active', state: 'ok' },
  { id: 'sessions', label: 'ACTIVE SESSIONS', value: '3', subline: 'claude · codex · codex', state: 'normal' },
  { id: 'issues', label: 'OPEN ISSUES', value: '7', subline: 'p0: 1 · p1: 3 · p2: 3', state: 'normal' },
  { id: 'todos', label: 'OPEN TODOS', value: '23', subline: 'across 4 repos', state: 'normal' },
];

const demoUrgentRows: ListRowData[] = [
  {
    id: '1',
    icon: <span style={{ color: '#ff6b81' }}>●</span>,
    meta: 'P0',
    title: 'DocFlow API latency spike — Civo LON1',
    priority: 'p0',
    actions: (
      <button style={{ background: 'var(--bg-surface-solid)', border: '1px solid var(--border-soft)', borderRadius: 'var(--radius-sm)', padding: '4px 10px', fontSize: '11px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
        reply
      </button>
    ),
  },
  {
    id: '2',
    icon: <span style={{ color: '#ff765c' }}>●</span>,
    meta: 'P1',
    title: 'SSL cert expires in 48h — dashboard.gameonsolutions.uk',
    priority: 'p1',
  },
];

const demoInboxRows: ListRowData[] = [
  { id: '1', meta: 'NOW', title: 'AWS billing alert', priority: 'p0' },
  { id: '2', meta: 'TODAY', title: 'PR review requested', priority: 'p1' },
  { id: '3', meta: 'WEEK', title: 'Newsletter draft', priority: 'p2' },
];

const demoCalendarRows: ListRowData[] = [
  { id: '1', meta: '14:00', title: 'Sprint Review — DocFlow' },
  { id: '2', meta: '16:30', title: 'Client Call — Acme Corp' },
  { id: '3', meta: '19:00', title: 'Dinner — Claire' },
];

interface DashboardLayoutProps {
  kpiCards?: KPICardData[];
  urgentRows?: ListRowData[];
  inboxRows?: ListRowData[];
  calendarRows?: ListRowData[];
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  kpiCards = demoKPICards,
  urgentRows = demoUrgentRows,
  inboxRows = demoInboxRows,
  calendarRows = demoCalendarRows,
}) => {
  const { mode } = useFocusMode();
  const { isDemo } = useDemoMode();

  // Hierarchy evaluation for panels
  const urgentHierarchy = usePanelHierarchy({
    panelId: 'urgent-panel',
    state: { hasUrgentItems: urgentRows.length > 0, itemCount: urgentRows.length, lastUpdated: new Date(), hasP0Items: urgentRows.some(r => r.priority === 'p0') },
  });

  const inboxHierarchy = usePanelHierarchy({
    panelId: 'inbox-panel',
    state: { hasUrgentItems: false, itemCount: inboxRows.length, lastUpdated: new Date(), unreadCount: inboxRows.length },
  });

  return (
    <div className="dashboard-grid">
      {/* KPI Strip - full width */}
      <div style={{ gridColumn: '1 / -1' }}>
        <KPIStrip cards={kpiCards.map(c => ({ ...c, state: isDemo ? 'normal' : c.state }))} />
      </div>

      {/* Main Column */}
      <div className="col-main" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Urgent Panel */}
        <div data-focus={mode === 'ship' || mode === 'triage' ? 'true' : undefined}>
          <ListPanel
            title="URGENT"
            badge={`${urgentRows.length} items`}
            rows={urgentRows}
            hierarchy={urgentHierarchy}
            emptyState={{ icon: 'circle', title: 'no urgent items', body: "everything's quiet. check back tomorrow." }}
            demo={isDemo}
          />
        </div>

        {/* Infrastructure Panel */}
        <div data-focus={mode === 'ship' ? 'true' : undefined}>
          <PanelWrapper title="INFRASTRUCTURE" hierarchy="secondary" demo={isDemo}>
            <div style={{ padding: '16px' }}>
              <div className="text-body-sm" style={{ color: 'var(--ok)' }}>DocFlow · 4 apps · all online</div>
              <div className="text-body-sm" style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>Hetzner · 2 VMs · 1 stopping</div>
              <div className="text-body-sm" style={{ color: 'var(--ok)', marginTop: '8px' }}>Railway · 3 services · healthy</div>
            </div>
          </PanelWrapper>
        </div>

        {/* Tonight's Focus */}
        <div data-focus={mode === 'ship' ? 'true' : undefined}>
          <PanelWrapper
            title="TONIGHT'S FOCUS"
            hierarchy="primary"
            badge="3 items"
            demo={isDemo}
          >
            <div style={{ padding: '24px' }}>
              <div className="text-caption" style={{ color: 'var(--accent)', marginBottom: '12px' }}>PICKED BY MERCEDES</div>
              <div className="text-h3" style={{ marginBottom: '8px' }}>1. Fix DocFlow API latency</div>
              <div className="text-body-sm" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>from urgent · civo lon1</div>
              <div className="text-h3" style={{ marginBottom: '8px' }}>2. Review Claire calendar</div>
              <div className="text-body-sm" style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>from home tab</div>
              <div className="text-h3" style={{ marginBottom: '8px' }}>3. Deploy dashboard v2</div>
              <div className="text-body-sm" style={{ color: 'var(--text-secondary)' }}>from tasks</div>
            </div>
          </PanelWrapper>
        </div>
      </div>

      {/* Side Column */}
      <div className="col-side" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {/* Calendar */}
        <div data-focus={mode === 'triage' ? 'true' : undefined}>
          <ListPanel
            title="NEXT 24H"
            rows={calendarRows}
            hierarchy="secondary"
            emptyState={{ icon: 'square', title: 'no upcoming events', body: 'calendar is clear.' }}
            demo={isDemo}
          />
        </div>

        {/* Inbox */}
        <div data-focus={mode === 'triage' ? 'true' : undefined}>
          <ListPanel
            title="INBOX"
            badge={`${inboxRows.length} unread`}
            rows={inboxRows}
            hierarchy={inboxHierarchy}
            emptyState={{ icon: 'checkmark', title: 'inbox zero', body: 'ai classified everything. nothing needs you now.' }}
            demo={isDemo}
          />
        </div>

        {/* Issues */}
        <div data-focus={mode === 'triage' ? 'true' : undefined}>
          <ListPanel
            title="ISSUES"
            badge="7 open"
            rows={[
              { id: '1', meta: 'P0', title: 'API latency spike', priority: 'p0' },
              { id: '2', meta: 'P1', title: 'SSL cert expiry', priority: 'p1' },
            ]}
            hierarchy="secondary"
            demo={isDemo}
          />
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
